/**
 * @module ServiceTestsScheduleTransforms
 * @description Phase 11 extraction of screen↔API schedule transforms and validation helpers.
 * Centralizes the schedule shape used by TestRunScheduleSection and save flows.
 */

import moment from 'moment-timezone';
import { getTimeZone } from '../../../stores/SessionStore';
import {
    DAILY,
    DAILY_TYPE_FORMAT,
    DEFAULT_INTERVAL_SECONDS,
    INTERVAL,
    INTERVAL_TYPE_FORMAT,
    SCHEDULE_ERROR_KEYS,
    WINDOW,
} from './ServiceTestsScheduleConstants';

/**
 * Convert 'HH:mm' stored in a given IANA timezone to 'HH:mm' in the user's timezone.
 * Returns undefined when timeString is falsy.
 * @param {string|null} timeString - 24h 'HH:mm'
 * @param {string|null} fromTz - IANA TZ the time is stored in (e.g. 'Asia/Shanghai')
 * @returns {string|undefined}
 */
// Note: DateTimeUtilities.convertTimeToUserTZ cannot be reused here because it appends
// a TZ abbreviation suffix and returns '' for empty — this helper needs bare 'HH:mm' and undefined.
function convertTimeToScreenHHMM(timeString, fromTz) {
    if (!timeString) return undefined;
    const [hourStr, minuteStr] = timeString.split(':');
    const hours = Number.parseInt(hourStr, 10);
    const minutes = Number.parseInt(minuteStr, 10);
    // Guard against malformed API data (e.g. "invalid:data" → NaN) to avoid moment producing "Invalid date"
    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
        return undefined;
    }
    const sourceTz = fromTz || 'UTC';
    const userTz = getTimeZone();
    return moment.tz({ hours, minutes, seconds: 0 }, sourceTz).tz(userTz).format('HH:mm');
}

/**
 * Resolve a stored interval (seconds) to the nearest valid option in the filtered list.
 *
 * Resolution rules (matching portal/ui behavior):
 * - No stored value → default to 600s if in range, else the first (smallest) option
 * - Exact match → use it
 * - Value between options → round up to the next available option
 * - Value above all options → use the highest available option
 *
 * @param {number|null|undefined} intervalSeconds
 * @param {Array<{ value: number, text: string }>} intervals - already filtered by range
 * @returns {number}
 */
function getIntervalForScreen(intervalSeconds, intervals) {
    if (!intervals || intervals.length === 0) return DEFAULT_INTERVAL_SECONDS;

    if (!intervalSeconds) {
        const defaultOption = intervals.find((i) => i.value === DEFAULT_INTERVAL_SECONDS);
        return defaultOption ? DEFAULT_INTERVAL_SECONDS : intervals[0].value;
    }

    if (intervals.some((i) => i.value === intervalSeconds)) return intervalSeconds;

    const nextHigher = intervals.find((i) => i.value > intervalSeconds);
    if (nextHigher) return nextHigher.value;

    return intervals.at(-1).value;
}

/**
 * Transform raw API scheduling data into dialog form state.
 * Handles empty scheduling ({}) for multi-record selection by defaulting to Interval mode.
 *
 * @param {Object} scheduling - Raw scheduling object from the API
 * @param {Array<{ value: number, text: string }>} intervals - Filtered interval list
 * @returns {{
 *   scheduleType: string,
 *   interval: number|string,
 *   isWindow: boolean,
 *   windowStart: string,
 *   windowEnd: string,
 *   dailyStart: string
 * }}
 */
export function prepareScheduleForScreen(scheduling, intervals) {
    const type = scheduling?.type;
    const fromTz = scheduling?.timezone;

    if (type === DAILY) {
        return {
            scheduleType: DAILY,
            interval: '',
            isWindow: false,
            windowStart: '',
            windowEnd: '',
            dailyStart: convertTimeToScreenHHMM(scheduling.start, fromTz) || '',
        };
    }

    if (type === WINDOW) {
        return {
            scheduleType: INTERVAL,
            interval: getIntervalForScreen(scheduling.interval, intervals),
            isWindow: true,
            windowStart: convertTimeToScreenHHMM(scheduling.start, fromTz) || '',
            windowEnd: convertTimeToScreenHHMM(scheduling.end, fromTz) || '',
            dailyStart: '',
        };
    }

    // INTERVAL or empty scheduling (multi-record selection) — default to Interval mode
    return {
        scheduleType: INTERVAL,
        interval: getIntervalForScreen(scheduling?.interval, intervals),
        isWindow: false,
        windowStart: '',
        windowEnd: '',
        dailyStart: '',
    };
}

// Flat schedule fields read by prepareScheduleForSave — exported so callers
// can strip them from payloads that should not contain them.
export const SCHEDULE_FLAT_FIELDS = ['scheduleType', INTERVAL, 'isWindow', 'windowStart', 'windowEnd', 'dailyStart'];

/**
 * Transform dialog form state into the scheduling payload for updateDoc.scheduling.
 * The caller is responsible for wrapping this into the full API request body.
 *
 * @param {{
 *   scheduleType: string,
 *   interval: number|undefined,
 *   isWindow: boolean,
 *   windowStart: string|undefined,
 *   windowEnd: string|undefined,
 *   dailyStart: string|undefined
 * }} formState
 * @returns {Object} scheduling payload ready for the API
 */
export function prepareScheduleForSave(formState) {
    const { scheduleType, interval, isWindow, windowStart, windowEnd, dailyStart } = formState;
    const userTz = getTimeZone();

    // typeFormat is consumed by ScheduleFormatters.formatScheduleType() for display.
    // scheduleType and isWindow are legacy fields retained for API compatibility with the portal/ui backend.
    if (scheduleType === DAILY) {
        return {
            scheduling: {
                type: DAILY,
                typeFormat: DAILY_TYPE_FORMAT,
                scheduleType: DAILY,
                isWindow: false,
                start: dailyStart,
                dailyStart,
                timezone: userTz,
            },
        };
    }

    if (isWindow) {
        return {
            scheduling: {
                type: WINDOW,
                typeFormat: INTERVAL_TYPE_FORMAT,
                scheduleType: INTERVAL,
                interval,
                isWindow: true,
                start: windowStart,
                end: windowEnd,
                windowStart,
                windowEnd,
                timezone: userTz,
            },
        };
    }

    return {
        scheduling: {
            type: INTERVAL,
            typeFormat: INTERVAL_TYPE_FORMAT,
            scheduleType: INTERVAL,
            interval,
            isWindow: false,
        },
    };
}

/**
 * Validate form state. Returns a map of fieldName → error key constant.
 * An empty object means the form is valid.
 *
 * @param {{
 *   scheduleType: string,
 *   interval: number|undefined,
 *   isWindow: boolean,
 *   windowStart: string|undefined,
 *   windowEnd: string|undefined,
 *   dailyStart: string|undefined
 * }} formState
 * @returns {Object}
 */
export function validateSchedule(formState) {
    const { scheduleType, interval, isWindow, windowStart, windowEnd, dailyStart } = formState;
    const errors = {};

    if (scheduleType === DAILY) {
        if (!dailyStart) {
            errors.dailyStart = SCHEDULE_ERROR_KEYS.DAILY_START_REQUIRED;
        }
        return errors;
    }

    // Interval mode (use == null to avoid false positive when interval is 0)
    if (interval == null) {
        errors.interval = SCHEDULE_ERROR_KEYS.INTERVAL_REQUIRED;
    }

    if (isWindow) {
        if (!windowStart) {
            errors.windowStart = SCHEDULE_ERROR_KEYS.WINDOW_START_REQUIRED;
        }
        if (!windowEnd) {
            errors.windowEnd = SCHEDULE_ERROR_KEYS.WINDOW_END_REQUIRED;
        }
        if (windowStart && windowEnd && windowStart === windowEnd) {
            // Intentionally only checks equality, not ordering — midnight-crossing windows
            // (e.g., start=22:00, end=06:00) are valid in this API.
            errors.windowEnd = SCHEDULE_ERROR_KEYS.WINDOW_END_SAME_AS_START;
        }
    }

    return errors;
}
